import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "../../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { Input } from "../../../app/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "../../../app/components/ui/alert";
import { Checkbox } from "../../../app/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../app/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../app/components/ui/select";
import { useAuth } from "../../../providers/AuthProvider";
import { useCategories } from "./hooks/useCategories";
import { CategoryFormModal } from "./components/CategoryFormModal";
import type { CategoryFlat } from "./utils/buildCategoryTree";
import {
  collectCategoryTreeIds,
  filterCategoryTree,
  flattenVisibleCategoryRows,
} from "./utils/buildCategoryTree";
import { downloadCsv } from "../utils/inventoryBulkCsv";
import { getCsvColumnIndex, parseCsvWithHeader } from "../../../lib/csv";
import { api } from "../../../lib/api";

export function CategoryPage() {
  const { user, token } = useAuth();
  const {
    categories,
    categoriesTree,
    loading,
    error,
    reload,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useCategories();

  const isBusinessOwner = user?.role === "business_owner";
  const isReadOnly = !isBusinessOwner;

  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [currentParentId, setCurrentParentId] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<CategoryFlat | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkWorking, setBulkWorking] = useState(false);
  const [bulkParentId, setBulkParentId] = useState<string>("__keep__");
  const importRef = useRef<HTMLInputElement | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const filteredTree = useMemo(() => filterCategoryTree(categoriesTree, search), [categoriesTree, search]);

  useEffect(() => {
    const next = new Set<string>();
    if (search.trim()) {
      for (const id of collectCategoryTreeIds(filteredTree)) {
        next.add(id);
      }
    } else {
      for (const n of filteredTree) {
        next.add(n.id);
      }
    }
    setExpandedIds(next);
  }, [filteredTree, search]);

  const visibleRows = useMemo(
    () => flattenVisibleCategoryRows(filteredTree, expandedIds),
    [filteredTree, expandedIds],
  );

  const visibleIds = useMemo(() => visibleRows.map((r) => r.node.id), [visibleRows]);

  const selectedCount = selectedIds.size;
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(String(id)));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(String(id)));

  const idToName = useMemo(() => new Map(categories.map((c) => [String(c.id), c.name])), [categories]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAllVisible = () => setExpandedIds(new Set(collectCategoryTreeIds(filteredTree)));
  const collapseAll = () => setExpandedIds(new Set());

  const handleAddRoot = () => {
    if (isReadOnly) return;
    setModalMode("create");
    setCurrentParentId(null);
    setEditingCategory(null);
    setMutationError(null);
    setModalOpen(true);
  };

  const handleAddSubcategory = (node: CategoryFlat) => {
    if (isReadOnly) return;
    setModalMode("create");
    setCurrentParentId(node.id);
    setEditingCategory(null);
    setMutationError(null);
    setModalOpen(true);
  };

  const handleEditCategory = (node: CategoryFlat) => {
    if (isReadOnly) return;
    setModalMode("edit");
    setCurrentParentId(node.parent_id);
    setEditingCategory(node);
    setMutationError(null);
    setModalOpen(true);
  };

  const handleDeleteCategory = async (node: CategoryFlat) => {
    if (isReadOnly) return;
    setDeleteError(null);
    try {
      const confirmed = window.confirm("Are you sure you want to delete this category?");
      if (!confirmed) return;
      await deleteCategory(node.id);
    } catch (e) {
      const err = e as Error & { code?: string };
      if (err.code === "HAS_CHILDREN") {
        setDeleteError("Cannot delete a category that has subcategories. Please delete its subcategories first.");
      } else {
        setDeleteError(err.message || "Failed to delete category.");
      }
    }
  };

  const handleSubmitModal = async (values: { id?: string; name: string; parent_id: string | null }) => {
    setMutationError(null);
    try {
      if (modalMode === "create") {
        await createCategory({ name: values.name, parent_id: values.parent_id });
      } else if (modalMode === "edit" && editingCategory) {
        await updateCategory(editingCategory.id, { name: values.name, parent_id: values.parent_id });
      }
      setModalOpen(false);
      setEditingCategory(null);
      setCurrentParentId(null);
    } catch (e) {
      const err = e as Error & { data?: { errors?: Record<string, string[]> } };
      if (err.data?.errors?.name?.length) {
        setMutationError(err.data.errors.name[0]);
      } else {
        setMutationError(err.message || "Failed to save category.");
      }
    }
  };

  const initialModalValues: { id?: string; name: string; parent_id: string | null } =
    modalMode === "edit" && editingCategory
      ? { id: editingCategory.id, name: editingCategory.name, parent_id: editingCategory.parent_id }
      : { name: "", parent_id: currentParentId };

  const parentOptions = categories
    .filter((c) => c.id !== (editingCategory?.id ?? ""))
    .map((c) => ({ id: c.id, name: c.name }));

  const toggleRow = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(String(id));
      else next.delete(String(id));
      return next;
    });
  };

  const toggleSelectAllVisible = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of visibleIds) {
        if (checked) next.add(String(id));
        else next.delete(String(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const exportAllCategories = () => {
    if (categories.length === 0) return;
    const rows = categories.map((c) => {
      const parentName = c.parent_id ? idToName.get(String(c.parent_id)) ?? "" : "";
      return [c.name, parentName, String(c.product_count ?? 0)];
    });
    const header = ["name", "parent_name", "product_count"];
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const csv = [header.join(","), ...rows.map((r) => r.map((x) => (x.includes(",") ? `"${x.replace(/\"/g, "\"\"")}"` : x)).join(","))].join("\r\n");
    downloadCsv(`categories-export-all-${stamp}.csv`, csv);
  };

  const exportSelected = () => {
    const rows = categories
      .filter((c) => selectedIds.has(String(c.id)))
      .map((c) => {
        const parentName = c.parent_id ? idToName.get(String(c.parent_id)) ?? "" : "";
        return [c.name, parentName, String(c.product_count ?? 0)];
      });
    const header = ["name", "parent_name", "product_count"];
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const csv = [header.join(","), ...rows.map((r) => r.map((x) => (x.includes(",") ? `"${x.replace(/\"/g, "\"\"")}"` : x)).join(","))].join("\r\n");
    downloadCsv(`categories-export-selected-${stamp}.csv`, csv);
  };

  const bulkAssignParent = async () => {
    if (!token || isReadOnly || selectedIds.size === 0) return;
    if (bulkParentId === "__keep__") return;
    setBulkWorking(true);
    setDeleteError(null);
    try {
      await api.categories.bulkAssignParent({
        token,
        ids: Array.from(selectedIds),
        parent_id: bulkParentId === "__root__" ? null : bulkParentId,
      });
      await reload();
      clearSelection();
      setBulkParentId("__keep__");
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "Failed to assign parent");
    } finally {
      setBulkWorking(false);
    }
  };

  const bulkDeleteSelected = async () => {
    if (!token || isReadOnly || selectedIds.size === 0) return;
    const ok = window.confirm("Soft delete selected categories? Products will be moved to Uncategorized.");
    if (!ok) return;
    setBulkWorking(true);
    setDeleteError(null);
    try {
      await api.categories.bulkDelete({ token, ids: Array.from(selectedIds) });
      await reload();
      clearSelection();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "Failed to delete categories");
    } finally {
      setBulkWorking(false);
    }
  };

  const importCsv = async (file: File) => {
    if (!token || isReadOnly) return;
    const text = await file.text();
    const parsed = parseCsvWithHeader(text);
    const nameIdx = getCsvColumnIndex(parsed.header, "name");
    const parentIdx = getCsvColumnIndex(parsed.header, "parent_name");
    if (nameIdx < 0) throw new Error("CSV must have a 'name' column");

    const existingByName = new Map(categories.map((c) => [c.name.trim().toLowerCase(), c]));
    for (const r of parsed.rows) {
      const name = (r[nameIdx] ?? "").trim();
      if (!name) continue;
      if (existingByName.has(name.toLowerCase())) continue;
      await createCategory({ name, parent_id: null });
      await reload();
    }

    if (parentIdx >= 0) {
      await reload();
      const afterByName = new Map(categories.map((c) => [c.name.trim().toLowerCase(), c]));
      for (const r of parsed.rows) {
        const name = (r[nameIdx] ?? "").trim();
        const parentName = (r[parentIdx] ?? "").trim();
        if (!name || !parentName) continue;
        const cat = afterByName.get(name.toLowerCase());
        const parent = afterByName.get(parentName.toLowerCase());
        if (cat && parent) {
          await updateCategory(cat.id, { parent_id: parent.id });
        }
      }
    }

    await reload();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-foreground mb-1">Categories</h1>
          <p className="text-muted-foreground">Manage your product categories and hierarchy.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" onClick={exportAllCategories} disabled={loading || categories.length === 0}>
            Export all
          </Button>
          <Button type="button" onClick={handleAddRoot} disabled={isReadOnly}>
            Add Root Category
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Categories</CardTitle>
          <CardDescription>Tree view, bulk actions, import/export, and product counts in one place.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <Input
              placeholder="Search categories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-md"
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={expandAllVisible}>
                Expand all
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={collapseAll}>
                Collapse all
              </Button>
              <Button type="button" variant="outline" disabled={isReadOnly} onClick={() => importRef.current?.click()}>
                Import CSV
              </Button>
              <input
                ref={importRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  void importCsv(f).catch((err) => setMutationError(err instanceof Error ? err.message : "Failed to import CSV"));
                  e.currentTarget.value = "";
                }}
              />
              <Button type="button" variant="outline" onClick={exportSelected} disabled={selectedCount === 0}>
                Export selected
              </Button>
            </div>
          </div>

          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Failed to load categories</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          {deleteError ? (
            <Alert variant="destructive">
              <AlertTitle>Unable to delete category</AlertTitle>
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          ) : null}

          {selectedCount > 0 ? (
            <div className="flex flex-wrap items-center gap-2 p-3 border rounded-md bg-muted/30">
              <span className="text-sm font-medium">{selectedCount} selected</span>
              <Button type="button" size="sm" variant="outline" onClick={clearSelection} disabled={bulkWorking}>
                Clear selection
              </Button>
              <div className="flex items-center gap-2">
                <Select value={bulkParentId} onValueChange={setBulkParentId} disabled={bulkWorking || isReadOnly}>
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Assign parent..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__keep__">Assign parent…</SelectItem>
                    <SelectItem value="__root__">No parent (root)</SelectItem>
                    {categories
                      .filter((c) => !selectedIds.has(String(c.id)))
                      .map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void bulkAssignParent()}
                  disabled={bulkWorking || isReadOnly || bulkParentId === "__keep__"}
                >
                  Apply
                </Button>
              </div>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={() => void bulkDeleteSelected()}
                disabled={bulkWorking || isReadOnly}
              >
                Delete selected
              </Button>
            </div>
          ) : null}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false}
                    onCheckedChange={(v) => toggleSelectAllVisible(v === true)}
                    aria-label="Select all visible categories"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Parent</TableHead>
                <TableHead className="text-right w-24">Products</TableHead>
                <TableHead className="text-right min-w-[20rem] pl-6">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && !categories.length ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground">
                    No categories found.
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map(({ node, depth }) => {
                  const hasChildren = node.children.length > 0;
                  const expanded = expandedIds.has(node.id);
                  const pc = node.product_count ?? 0;
                  return (
                    <TableRow key={node.id}>
                      <TableCell className="align-middle py-3">
                        <Checkbox
                          checked={selectedIds.has(String(node.id))}
                          onCheckedChange={(v) => toggleRow(String(node.id), v === true)}
                        />
                      </TableCell>
                      <TableCell className="align-middle py-3">
                        <div className="flex items-center gap-1 min-w-0" style={{ paddingLeft: depth * 14 }}>
                          {hasChildren ? (
                            <button
                              type="button"
                              onClick={() => toggleExpand(node.id)}
                              className="flex shrink-0 items-center justify-center rounded-xs p-1 text-muted-foreground hover:bg-accent/70 hover:text-foreground"
                              aria-label={expanded ? "Collapse" : "Expand"}
                            >
                              {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                            </button>
                          ) : (
                            <span className="inline-block w-6 shrink-0" />
                          )}
                          <span className="truncate font-medium">{node.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground align-middle py-3">
                        {node.parent_id ? idToName.get(String(node.parent_id)) ?? "—" : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums align-middle py-3">{pc}</TableCell>
                      <TableCell className="text-right align-middle py-3 pl-6">
                        {!isReadOnly ? (
                          <div className="flex flex-wrap items-center justify-end gap-x-2.5 gap-y-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-9 shrink-0 px-3 text-xs"
                              onClick={() => handleAddSubcategory(node)}
                            >
                              <Plus className="mr-1.5 size-3.5" />
                              Subcategory
                            </Button>
                            <Button type="button" variant="ghost" size="sm" className="h-9 shrink-0 px-3 text-xs" onClick={() => handleEditCategory(node)}>
                              <Pencil className="mr-1.5 size-3.5" />
                              Edit
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-9 shrink-0 px-3 text-xs text-destructive hover:text-destructive"
                              onClick={() => void handleDeleteCategory(node)}
                            >
                              <Trash2 className="mr-1.5 size-3.5" />
                              Delete
                            </Button>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CategoryFormModal
        open={modalOpen}
        mode={modalMode}
        initialValues={initialModalValues}
        parentOptions={parentOptions}
        onOpenChange={setModalOpen}
        onSubmit={handleSubmitModal}
        error={mutationError}
      />
    </div>
  );
}
